import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, FileText, Sparkles, Plus, Video, Link as LinkIcon, Download, ChevronRight } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../common/Toast';
import ProjectIntelligenceWorkspaceShell from './ProjectIntelligenceWorkspaceShell';
import { getPiWorkspacePath } from './projectIntelligencePhases';
import '../../styles/dpr-premium.css';

export default function ProjectInitiationWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error } = useToast();

  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    projectTitle: '',
    department: '',
    projectType: '',
    estimatedValue: '',
    location: '',
    surveyReference: '',
    problemStatement: '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (id) fetchProject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/sourcing/project-requirements/${id}`);
      if (res.data.success) {
        const p = res.data.data;
        setProject(p);
        setFormData({
          projectTitle: p.projectTitle || '',
          department: p.department || '',
          projectType: p.projectType || p.category || '',
          estimatedValue: p.estimatedValue || '',
          location: p.location || '',
          surveyReference: p.surveyReference || '',
          problemStatement: p.problemStatement || '',
        });
      } else {
        setProject(null);
      }
    } catch (err) {
      error('Failed to load project');
      setProject(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await api.put(`/sourcing/project-requirements/${id}/initiation`, formData);
      if (res.data.success) {
        success('Project details saved');
        fetchProject();
      }
    } catch (err) {
      error('Failed to save project details');
    } finally {
      setSaving(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const data = new FormData();
    data.append('file', file);
    data.append('type', file.type.includes('video') ? 'video' : file.type.includes('pdf') ? 'pdf' : 'doc');

    try {
      setUploading(true);
      const res = await api.post(`/sourcing/project-requirements/${id}/contributions`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success) {
        success('Contribution added');
        fetchProject();
      }
    } catch (err) {
      error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const headerActions = [
    {
      label: saving ? 'Saving...' : 'Save Details',
      icon: <Save size={18} />,
      variant: 'outline',
      onClick: handleSave,
      disabled: saving,
    },
    {
      label: 'Proceed to DPR Builder',
      icon: <ChevronRight size={18} />,
      variant: 'primary',
      onClick: () => navigate(getPiWorkspacePath('builder', id)),
    },
  ];

  const themes = project?.aiAnalysis?.themes;
  const gaps = project?.aiAnalysis?.gaps;

  return (
    <ProjectIntelligenceWorkspaceShell
      phase="initiation"
      projectId={id}
      projectTitle={project?.projectTitle}
      loading={loading}
      notFound={!loading && !project}
      actions={project ? headerActions : []}
    >
      <div className="row g-4">
        <div className="col-lg-7">
          <h5 className="fw-bold mb-3">Project details</h5>
          <div className="pi-card mb-4">
            <div className="pi-card-body">
              <div className="form-grid-2">
                <div>
                  <label className="form-label fw-medium">Project name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.projectTitle}
                    onChange={(e) => setFormData({ ...formData, projectTitle: e.target.value })}
                  />
                </div>
                <div>
                  <label className="form-label fw-medium">Department</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  />
                </div>
                <div>
                  <label className="form-label fw-medium">Project type</label>
                  <select
                    className="form-select"
                    value={formData.projectType}
                    onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
                  >
                    <option value="">Select type</option>
                    <option value="Water Infrastructure">Water Infrastructure</option>
                    <option value="Roads">Roads</option>
                    <option value="IT Software">IT Software</option>
                    <option value="Civic">Civic</option>
                  </select>
                </div>
                <div>
                  <label className="form-label fw-medium">Estimated value (₹)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.estimatedValue}
                    onChange={(e) => setFormData({ ...formData, estimatedValue: e.target.value })}
                  />
                </div>
                <div>
                  <label className="form-label fw-medium">Location / jurisdiction</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  />
                </div>
                <div>
                  <label className="form-label fw-medium">Survey / plot reference</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.surveyReference}
                    onChange={(e) => setFormData({ ...formData, surveyReference: e.target.value })}
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-medium">Initiating officer</label>
                <div className="d-flex align-items-center gap-3 p-3 border rounded bg-light">
                  <div
                    className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                    style={{ width: 40, height: 40, fontWeight: 'bold' }}
                  >
                    {project?.owner?.firstName?.[0] || 'U'}
                  </div>
                  <div>
                    <div className="fw-semibold">
                      {project?.owner?.firstName} {project?.owner?.lastName}
                    </div>
                    <div className="small text-muted">Project owner</div>
                  </div>
                </div>
              </div>

              <div>
                <label className="form-label fw-medium">Problem statement</label>
                <textarea
                  className="form-control"
                  rows="4"
                  value={formData.problemStatement}
                  onChange={(e) => setFormData({ ...formData, problemStatement: e.target.value })}
                  placeholder="Describe the problem this project solves..."
                />
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-5">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-bold m-0">Team contributions</h5>
            <label className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1 m-0">
              <Plus size={16} /> Add
              <input type="file" className="d-none" onChange={handleFileChange} disabled={uploading} />
            </label>
          </div>

          <div className="d-flex flex-column gap-2 mb-4">
            {!project?.contributions?.length && (
              <div className="text-center p-4 text-muted border border-dashed rounded bg-white">
                No contributions yet. Upload documents or videos to enrich the project brief.
              </div>
            )}
            {project?.contributions?.map((c, i) => (
              <div
                key={i}
                className="pi-card pi-card-body d-flex align-items-center gap-3 py-3"
              >
                <div
                  className={`p-2 rounded ${
                    c.type === 'pdf'
                      ? 'bg-danger-subtle text-danger'
                      : c.type === 'video'
                        ? 'bg-primary-subtle text-primary'
                        : 'bg-success-subtle text-success'
                  }`}
                >
                  {c.type === 'pdf' ? (
                    <FileText size={20} />
                  ) : c.type === 'video' ? (
                    <Video size={20} />
                  ) : c.type === 'link' ? (
                    <LinkIcon size={20} />
                  ) : (
                    <FileText size={20} />
                  )}
                </div>
                <div className="flex-grow-1 min-w-0">
                  <div className="fw-semibold text-truncate">{c.fileName || 'Text note'}</div>
                  <div className="text-muted small">
                    {new Date(c.addedAt).toLocaleDateString()}
                  </div>
                </div>
                <button type="button" className="btn btn-sm btn-light" aria-label="Download">
                  <Download size={16} />
                </button>
              </div>
            ))}
          </div>

          <div className="pi-alert-banner pi-alert-banner--info">
            <div className="d-flex align-items-start gap-2 mb-2">
              <Sparkles size={20} className="text-primary flex-shrink-0 mt-1" />
              <div>
                <h6 className="fw-bold mb-1">AI brief insights</h6>
                <p className="small text-muted mb-0">
                  {project?.contributions?.length || 0} contribution(s) on record.
                </p>
              </div>
            </div>
            {themes?.length > 0 ? (
              <p className="small mb-2">
                <strong>Key themes:</strong> {themes.join(', ')}
              </p>
            ) : (
              <p className="small text-muted mb-2">
                Save project details and add contributions to build context for DPR generation.
              </p>
            )}
            {gaps?.length > 0 && (
              <p className="small mb-0 text-warning-emphasis">
                <strong>Gaps:</strong> {gaps.join('; ')}
              </p>
            )}
          </div>
        </div>
      </div>
    </ProjectIntelligenceWorkspaceShell>
  );
}
